import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Store } from '../types';
import {
  Store as StoreIcon, MapPin, Phone, Mail, Plus, Pencil,
  Trash2, X, RotateCcw, Clock, ShoppingCart, Upload, ImageOff, Scale,
} from 'lucide-react';
import { getApiBase } from '../utils/apiBase';

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page:             { padding: '32px', background: '#f8f7f5', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" } as React.CSSProperties,
  header:           { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap' as const, gap: '12px' },
  title:            { margin: 0, fontSize: '28px', fontWeight: 600, color: '#111827' },
  sub:              { margin: '4px 0 0', fontSize: '14px', color: '#9ca3af' },
  addBtn:           { display: 'flex', alignItems: 'center', gap: '8px', background: '#F1561D', color: '#fff', border: 'none', borderRadius: '10px', padding: '10px 20px', fontSize: '14px', fontWeight: 500, cursor: 'pointer' },
  grid:             { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' },
  card:             { background: '#fff', borderRadius: '16px', border: '1px solid #f3f4f6', overflow: 'hidden', transition: 'box-shadow 0.2s, border-color 0.2s' },
  cardDeleted:      { background: '#fafafa', borderRadius: '16px', border: '1.5px dashed #d1d5db', overflow: 'hidden', opacity: 0.75 },
  cardTop:          { padding: '20px 20px 16px', borderBottom: '1px solid #f9fafb' },
  cardRow:          { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' },
  storeName:        { margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' },
  storeNameDeleted: { margin: 0, fontSize: '16px', fontWeight: 600, color: '#9ca3af', textDecoration: 'line-through' },
  desc:             { margin: '4px 0 0', fontSize: '13px', color: '#9ca3af' },
  meta:             { padding: '14px 20px', display: 'flex', flexDirection: 'column' as const, gap: '8px' },
  metaRow:          { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#6b7280' },
  metaRowFaded:     { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#c4c9d2' },
  cardFooter:       { padding: '12px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: '8px', justifyContent: 'flex-end' },
  editBtn:          { display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '13px', fontWeight: 500, color: '#374151', cursor: 'pointer' },
  delBtn:           { display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', border: '1px solid #fee2e2', background: '#fff5f5', fontSize: '13px', fontWeight: 500, color: '#dc2626', cursor: 'pointer' },
  restoreBtn:       { display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', border: '1px solid #bbf7d0', background: '#f0fdf4', fontSize: '13px', fontWeight: 500, color: '#16a34a', cursor: 'pointer' },
  pill:             (active: boolean) => ({ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 500, background: active ? '#d1fae5' : '#fee2e2', color: active ? '#065f46' : '#991b1b' }),
  pillDeleted:      { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 500, background: '#f3f4f6', color: '#6b7280' } as React.CSSProperties,
  dot:              (active: boolean) => ({ width: '6px', height: '6px', borderRadius: '50%', background: active ? '#10b981' : '#ef4444' }),
  overlay:          { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' },
  modal:            { background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '460px', maxHeight: '90vh', overflowY: 'auto' as const },
  modalHeader:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 0' },
  modalTitle:       { margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' },
  modalBody:        { padding: '20px 24px 24px', display: 'flex', flexDirection: 'column' as const, gap: '10px' },
  inp:              { width: '100%', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '9px 12px', fontSize: '14px', color: '#111827', outline: 'none', boxSizing: 'border-box' as const },
  inpMono:          { width: '100%', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '9px 12px', fontSize: '13px', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, fontFamily: 'monospace', letterSpacing: '0.03em' },
  label:            { fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '2px', display: 'block' },
  modalFooter:      { padding: '0 24px 20px', display: 'flex', gap: '8px', justifyContent: 'flex-end' },
  cancelBtn:        { padding: '9px 18px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '14px', fontWeight: 500, color: '#374151', cursor: 'pointer' },
  saveBtn:          { padding: '9px 18px', borderRadius: '8px', border: 'none', background: '#F1561D', color: '#fff', fontSize: '14px', fontWeight: 500, cursor: 'pointer' },
  divider:          { margin: '4px 0', borderTop: '1px solid #f3f4f6' },
  sectionLabel:     { fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginTop: '6px' },
  coordRow:         { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
  subaccountHint:   { fontSize: '11px', color: '#9ca3af', marginTop: '3px' },
  secretPill:       { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 600, background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '3px 8px', letterSpacing: '0.02em' } as React.CSSProperties,
  paymentBanner:    (configured: boolean) => ({ display: 'flex', flexDirection: 'column' as const, gap: '3px', padding: '10px 12px', borderRadius: '8px', border: configured ? '1px solid #bbf7d0' : '1px solid #fed7aa', background: configured ? '#f0fdf4' : '#fff7ed', color: configured ? '#166534' : '#9a3412', fontSize: '12px', lineHeight: 1.4 } as React.CSSProperties),
  deletedBanner:    { margin: '0 20px 12px', padding: '8px 12px', borderRadius: '8px', background: '#fef3c7', border: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#92400e' } as React.CSSProperties,
  // toggle
  toggleRow:        { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fafafa' } as React.CSSProperties,
  toggleLabel:      { display: 'flex', flexDirection: 'column' as const, gap: '2px' },
  toggleTitle:      { fontSize: '14px', color: '#111827', fontWeight: 500 },
  toggleHint:       { fontSize: '11px', color: '#9ca3af' },
  toggleTrack:      (on: boolean) => ({ position: 'relative' as const, width: '36px', height: '20px', borderRadius: '10px', background: on ? '#F1561D' : '#d1d5db', cursor: 'pointer', border: 'none', outline: 'none', transition: 'background 0.2s', flexShrink: 0 }),
  toggleThumb:      (on: boolean) => ({ position: 'absolute' as const, top: '3px', left: on ? '19px' : '3px', width: '14px', height: '14px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }),
  // logo
  logoWrap:         (deleted: boolean) => ({ width: '44px', height: '44px', borderRadius: '12px', overflow: 'hidden', flexShrink: 0, border: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', background: deleted ? '#f3f4f6' : '#fff5f2' } as React.CSSProperties),
  logoImg:          { width: '100%', height: '100%', objectFit: 'cover' as const },
  logoUploadRow:    { display: 'flex', alignItems: 'center', gap: '12px' } as React.CSSProperties,
  logoPreview:      { width: '52px', height: '52px', borderRadius: '10px', overflow: 'hidden', border: '1.5px dashed #e5e7eb', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', cursor: 'pointer' } as React.CSSProperties,
  uploadBtn:        { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '13px', fontWeight: 500, color: '#374151', cursor: 'pointer' } as React.CSSProperties,
  removeLogoBtn:    { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', border: '1px solid #fee2e2', background: '#fff5f5', fontSize: '13px', fontWeight: 500, color: '#dc2626', cursor: 'pointer' } as React.CSSProperties,
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function hoursRemaining(deletedAt: string | null | undefined): number | null {
  if (!deletedAt) return null;
  const ms = 48 * 60 * 60 * 1000 - (Date.now() - new Date(deletedAt).getTime());
  return ms > 0 ? Math.ceil(ms / (1000 * 60 * 60)) : null;
}

function hasPaystackSubaccount(store: any): boolean {
  return Boolean(store?.paystackSubaccountConfigured || store?.paystackSubaccount);
}

const API_BASE = getApiBase();

// ── Sub-components ────────────────────────────────────────────────────────────
const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({ label, children, hint }) => (
  <div>
    <span style={s.label}>{label}</span>
    {children}
    {hint && <p style={s.subaccountHint}>{hint}</p>}
  </div>
);

// Toggle switch
const Toggle: React.FC<{
  label: string;
  hint?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, hint, value, onChange }) => (
  <div style={s.toggleRow}>
    <div style={s.toggleLabel}>
      <span style={s.toggleTitle}>{label}</span>
      {hint && <span style={s.toggleHint}>{hint}</span>}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={value}
      style={s.toggleTrack(value)}
      onClick={() => onChange(!value)}
    >
      <span style={s.toggleThumb(value)} />
    </button>
  </div>
);

// Displays a store's logo, falling back to the StoreIcon
const StoreLogo: React.FC<{ logo?: string | null; name: string; deleted?: boolean }> = ({ logo, name, deleted = false }) => {
  const [errored, setErrored] = useState(false);
  useEffect(() => { setErrored(false); }, [logo]);

  return (
    <div style={s.logoWrap(deleted)}>
      {logo && !errored ? (
        <img
          src={logo}
          alt={`${name} logo`}
          style={s.logoImg}
          onError={() => setErrored(true)}
        />
      ) : (
        <StoreIcon size={20} color={deleted ? '#9ca3af' : '#F1561D'} />
      )}
    </div>
  );
};

// Logo upload widget used inside both modals
const LogoUploadField: React.FC<{
  currentLogo: string | null;
  storeId: string | null;
  token: string;
  onUploaded: (logo: string) => void;
  onPendingFile: (file: File | null) => void;
}> = ({ currentLogo, storeId, token, onUploaded, onPendingFile }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(
    currentLogo ? `${API_BASE}${currentLogo}` : null
  );
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setPreview(currentLogo ? `${API_BASE}${currentLogo}` : null);
  }, [currentLogo]);

  const validate = (file: File): boolean => {
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      alert('Please choose a JPG, PNG, or WebP image.');
      return false;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('Logo must be under 2 MB.');
      return false;
    }
    return true;
  };

  const handleFile = async (file: File) => {
    if (!validate(file)) return;
    setPreview(URL.createObjectURL(file));
    if (storeId) {
      await uploadNow(file, storeId);
    } else {
      onPendingFile(file);
    }
  };

  const uploadNow = async (file: File, id: string) => {
    setUploading(true);
    const form = new FormData();
    form.append('logo', file);
    try {
      const res = await fetch(`${API_BASE}/api/stores/${id}/logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await res.json();
      if (res.ok) onUploaded(data.logo);
      else alert(data.error || 'Upload failed');
    } catch {
      alert('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onPendingFile(null);
    onUploaded('');
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div style={s.logoUploadRow}>
      <div style={s.logoPreview} onClick={() => fileRef.current?.click()} title="Click to choose logo">
        {preview
          ? <img src={preview} alt="Logo preview" style={s.logoImg} />
          : <ImageOff size={20} color="#d1d5db" />}
      </div>
      <div style={{ flex: 1 }}>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
        />
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button
            type="button"
            style={{ ...s.uploadBtn, opacity: uploading ? 0.6 : 1 }}
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            <Upload size={13} />
            {uploading ? 'Uploading…' : preview ? 'Replace' : 'Choose logo'}
          </button>
          {preview && (
            <button type="button" style={s.removeLogoBtn} onClick={handleRemove}>
              <X size={13} /> Remove
            </button>
          )}
        </div>
        <p style={s.subaccountHint}>JPG, PNG or WebP · max 2 MB</p>
      </div>
    </div>
  );
};

// ── Types ─────────────────────────────────────────────────────────────────────
interface NewStoreForm {
  name: string;
  description: string;
  phone: string;
  email: string;
  line1: string;
  city: string;
  latitude: string;
  longitude: string;
  paystackSubaccount: string;
  maxCartQuantity: string;
  supportsWeighedProducts: boolean;
}

const EMPTY_NEW_STORE: NewStoreForm = {
  name: '', description: '', phone: '', email: '',
  line1: '', city: '', latitude: '', longitude: '',
  paystackSubaccount: '', maxCartQuantity: '',
  supportsWeighedProducts: false,
};

// ── Main component ────────────────────────────────────────────────────────────
const Stores: React.FC = () => {
  const [stores, setStores]               = useState<Store[]>([]);
  const [loading, setLoading]             = useState(true);

  // Add modal
  const [showModal, setShowModal]         = useState(false);
  const [newStore, setNewStore]           = useState<NewStoreForm>(EMPTY_NEW_STORE);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);

  // Edit modal
  const [editStore, setEditStore]         = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const token = localStorage.getItem('authToken') ?? '';

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchStores = useCallback(async () => {
    try {
      setLoading(true);
      const res  = await fetch(`${API_BASE}/api/stores?includeDeleted=true`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setStores(data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchStores(); }, [fetchStores]);

  // ── Mutations ──────────────────────────────────────────────────────────────
  const deleteStore = async (id: string) => {
    if (!window.confirm('Delete this store? You have 48 hours to restore it.')) return;
    try {
      const res = await fetch(`${API_BASE}/api/stores/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) await fetchStores();
    } catch (err) { console.error(err); }
  };

  const restoreStore = async (id: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/stores/${id}/restore`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        await fetchStores();
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to restore store.');
      }
    } catch (err) { console.error(err); }
  };

  // Add store
  const addStore = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      name:               newStore.name,
      description:        newStore.description,
      phone:              newStore.phone,
      email:              newStore.email,
      address:            { line1: newStore.line1, city: newStore.city },
      location:           { type: 'Point', coordinates: [Number(newStore.longitude), Number(newStore.latitude)] },
      paystackSubaccount: newStore.paystackSubaccount.trim() || null,
      settings: {
        maxCartQuantity:         newStore.maxCartQuantity !== '' ? Number(newStore.maxCartQuantity) : null,
        supportsWeighedProducts: newStore.supportsWeighedProducts,
      },
    };
    try {
      const res = await fetch(`${API_BASE}/api/stores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const created = await res.json();
        if (pendingLogoFile) {
          const form = new FormData();
          form.append('logo', pendingLogoFile);
          await fetch(`${API_BASE}/api/stores/${created._id}/logo`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: form,
          });
          setPendingLogoFile(null);
        }
        setShowModal(false);
        setNewStore(EMPTY_NEW_STORE);
        await fetchStores();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to add store.');
      }
    } catch (err) { console.error(err); }
  };

  // Edit store
  const saveEditStore = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      name:               editStore.name,
      description:        editStore.description,
      phone:              editStore.phone,
      email:              editStore.email,
      address:            { line1: editStore.address?.line1 || '', city: editStore.address?.city || '' },
      isActive:           editStore.isActive,
      location:           { type: 'Point', coordinates: [Number(editStore.location?.coordinates?.[0] ?? 0), Number(editStore.location?.coordinates?.[1] ?? 0)] },
      settings: {
        maxCartQuantity:
          editStore.settings?.maxCartQuantity != null && editStore.settings.maxCartQuantity !== ''
            ? Number(editStore.settings.maxCartQuantity)
            : null,
        supportsWeighedProducts: editStore.settings?.supportsWeighedProducts === true,
      },
    };
    const replacementSubaccount = editStore.paystackSubaccountReplacement?.trim();
    if (replacementSubaccount) {
      (payload as any).paystackSubaccount = replacementSubaccount;
    }
    try {
      const res = await fetch(`${API_BASE}/api/stores/${editStore._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setShowEditModal(false);
        await fetchStores();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to save store changes.');
      }
    } catch (err) { console.error(err); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) return <div style={{ padding: '32px', fontFamily: 'DM Sans, sans-serif', color: '#9ca3af' }}>Loading stores…</div>;

  const activeCount  = stores.filter(st => !(st as any).isDeleted && st.isActive).length;
  const sortedStores = [...stores].sort((a, b) => {
    const aD = !!(a as any).isDeleted;
    const bD = !!(b as any).isDeleted;
    if (aD !== bD) return aD ? 1 : -1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div style={s.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Stores</h1>
          <p style={s.sub}>{stores.length} location{stores.length !== 1 ? 's' : ''} · {activeCount} active</p>
        </div>
        <button style={s.addBtn} onClick={() => setShowModal(true)}>
          <Plus size={16} /> Add Store
        </button>
      </div>

      {/* ── Cards ───────────────────────────────────────────────────────────── */}
      {stores.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 0', color: '#9ca3af' }}>
          <StoreIcon size={40} style={{ margin: '0 auto 12px', display: 'block', opacity: 0.4 }} />
          <p style={{ margin: 0, fontSize: '16px' }}>No stores yet</p>
          <p style={{ margin: '4px 0 0', fontSize: '13px' }}>Add your first store to get started</p>
        </div>
      ) : (
        <div style={s.grid}>
          {sortedStores.map(store => {
            const isDeleted  = !!(store as any).isDeleted;
            const deletedAt: string | null = (store as any).deletedAt || null;
            const hrs        = hoursRemaining(deletedAt);
            const canRestore = hrs !== null;
            const supportsWeighed = !!(store as any).settings?.supportsWeighedProducts;
            const hasSubaccount = hasPaystackSubaccount(store);

            return (
              <div
                key={store._id}
                style={isDeleted ? s.cardDeleted : s.card}
                onMouseEnter={e => { if (!isDeleted) { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 24px rgba(0,0,0,0.08)'; (e.currentTarget as HTMLDivElement).style.borderColor = '#F1561D'; } }}
                onMouseLeave={e => { if (!isDeleted) { (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'; (e.currentTarget as HTMLDivElement).style.borderColor = '#f3f4f6'; } }}
              >
                {/* Card top */}
                <div style={s.cardTop}>
                  <div style={s.cardRow}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                      <StoreLogo logo={(store as any).logo} name={store.name} deleted={isDeleted} />
                      <div style={{ minWidth: 0 }}>
                        <p style={isDeleted ? s.storeNameDeleted : s.storeName}>{store.name}</p>
                        <p style={s.desc}>{store.description || 'No description'}</p>
                      </div>
                    </div>

                    {isDeleted ? (
                      <span style={s.pillDeleted}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#9ca3af' }} />
                        Deleted
                      </span>
                    ) : (
                      <span style={s.pill(store.isActive)}>
                        <span style={s.dot(store.isActive)} />
                        {store.isActive ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Deleted banner */}
                {isDeleted && (
                  <div style={s.deletedBanner}>
                    <Clock size={13} style={{ flexShrink: 0 }} />
                    {canRestore
                      ? `Store deleted · restore within ${hrs}h before it's permanently removed`
                      : 'Restore window expired · this store will be permanently deleted soon'}
                  </div>
                )}

                {/* Meta rows */}
                <div style={s.meta}>
                  {(store.address?.city || store.address?.line1) && (
                    <div style={isDeleted ? s.metaRowFaded : s.metaRow}>
                      <MapPin size={14} color="#d1d5db" style={{ flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {[store.address?.line1, store.address?.city].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                  {store.phone && (
                    <div style={isDeleted ? s.metaRowFaded : s.metaRow}>
                      <Phone size={14} color="#d1d5db" style={{ flexShrink: 0 }} />
                      <span>{store.phone}</span>
                    </div>
                  )}
                  {store.email && (
                    <div style={isDeleted ? s.metaRowFaded : s.metaRow}>
                      <Mail size={14} color="#d1d5db" style={{ flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{store.email}</span>
                    </div>
                  )}
                  {!isDeleted && hasSubaccount && (
                    <div style={{ ...s.metaRow, marginTop: '2px' }}>
                      <span style={s.secretPill}>Paystack code saved • hidden</span>
                    </div>
                  )}
                  {!isDeleted && (store as any).settings?.maxCartQuantity != null && (
                    <div style={s.metaRow}>
                      <ShoppingCart size={14} color="#d1d5db" style={{ flexShrink: 0 }} />
                      <span>Max {(store as any).settings.maxCartQuantity} items/cart</span>
                    </div>
                  )}
                  {!isDeleted && supportsWeighed && (
                    <div style={s.metaRow}>
                      <Scale size={14} color="#d1d5db" style={{ flexShrink: 0 }} />
                      <span>Supports weighed products</span>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div style={s.cardFooter}>
                  {isDeleted ? (
                    canRestore ? (
                      <button style={s.restoreBtn} onClick={() => restoreStore(store._id)}>
                        <RotateCcw size={13} /> Restore
                      </button>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#9ca3af', padding: '7px 0' }}>Permanently deleted soon</span>
                    )
                  ) : (
                    <>
                      <button style={s.editBtn} onClick={() => { setEditStore({ ...store, paystackSubaccountConfigured: hasSubaccount, paystackSubaccountReplacement: '' }); setShowEditModal(true); }}>
                        <Pencil size={13} /> Edit
                      </button>
                      <button style={s.delBtn} onClick={() => deleteStore(store._id)}>
                        <Trash2 size={13} /> Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Add Store Modal ──────────────────────────────────────────────────── */}
      {showModal && (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Add New Store</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={20} /></button>
            </div>
            <form onSubmit={addStore}>
              <div style={s.modalBody}>
                <Field label="Store Name">
                  <input style={s.inp} placeholder="e.g. Pick n Pay Bassonia" value={newStore.name} onChange={e => setNewStore({ ...newStore, name: e.target.value })} required />
                </Field>
                <Field label="Description">
                  <input style={s.inp} placeholder="Brief description" value={newStore.description} onChange={e => setNewStore({ ...newStore, description: e.target.value })} />
                </Field>

                <div style={s.divider} />
                <p style={s.sectionLabel}>Contact</p>
                <div style={s.coordRow}>
                  <Field label="Phone"><input style={s.inp} placeholder="+27…" value={newStore.phone} onChange={e => setNewStore({ ...newStore, phone: e.target.value })} /></Field>
                  <Field label="Email"><input style={s.inp} type="email" placeholder="store@email.com" value={newStore.email} onChange={e => setNewStore({ ...newStore, email: e.target.value })} /></Field>
                </div>

                <div style={s.divider} />
                <p style={s.sectionLabel}>Location</p>
                <Field label="Address"><input style={s.inp} placeholder="Street address" value={newStore.line1} onChange={e => setNewStore({ ...newStore, line1: e.target.value })} /></Field>
                <Field label="City"><input style={s.inp} placeholder="City" value={newStore.city} onChange={e => setNewStore({ ...newStore, city: e.target.value })} /></Field>
                <div style={s.coordRow}>
                  <Field label="Latitude"><input style={s.inp} type="number" step="any" placeholder="-26.1234" value={newStore.latitude} onChange={e => setNewStore({ ...newStore, latitude: e.target.value })} /></Field>
                  <Field label="Longitude"><input style={s.inp} type="number" step="any" placeholder="28.1234" value={newStore.longitude} onChange={e => setNewStore({ ...newStore, longitude: e.target.value })} /></Field>
                </div>

                <div style={s.divider} />
                <p style={s.sectionLabel}>Payments</p>
                <div style={s.paymentBanner(false)}>
                  <strong>No Paystack subaccount saved yet.</strong>
                  <span>Paste the store's Paystack ACCT_... code before taking routed payments for this store.</span>
                </div>
                <Field label="Paystack Subaccount Code" hint="Optional — e.g. ACCT_xxxxxxxxxxxxxxxx. This value is hidden after saving.">
                  <input style={s.inpMono} placeholder="ACCT_xxxxxxxxxxxxxxxx" value={newStore.paystackSubaccount} onChange={e => setNewStore({ ...newStore, paystackSubaccount: e.target.value })} />
                </Field>

                <div style={s.divider} />
                <p style={s.sectionLabel}>Cart Rules</p>
                <Field label="Max Items Per Cart" hint="Leave blank for unlimited. Customers cannot add more than this many items in a single session at this store.">
                  <input style={s.inp} type="number" min="1" step="1" placeholder="Unlimited" value={newStore.maxCartQuantity} onChange={e => setNewStore({ ...newStore, maxCartQuantity: e.target.value })} />
                </Field>
                <Toggle
                  label="Weighed products"
                  hint="Enable if this store sells items priced by weight (e.g. deli, produce)"
                  value={newStore.supportsWeighedProducts}
                  onChange={v => setNewStore({ ...newStore, supportsWeighedProducts: v })}
                />

                <div style={s.divider} />
                <p style={s.sectionLabel}>Logo</p>
                <LogoUploadField
                  currentLogo={null}
                  storeId={null}
                  token={token}
                  onUploaded={() => {}}
                  onPendingFile={setPendingLogoFile}
                />
              </div>
              <div style={s.modalFooter}>
                <button type="button" style={s.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" style={s.saveBtn}>Add Store</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit Store Modal ─────────────────────────────────────────────────── */}
      {showEditModal && editStore && (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) setShowEditModal(false); }}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Edit Store</h3>
              <button onClick={() => setShowEditModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={20} /></button>
            </div>
            <form onSubmit={saveEditStore}>
              <div style={s.modalBody}>
                <Field label="Store Name">
                  <input style={s.inp} value={editStore.name} onChange={e => setEditStore({ ...editStore, name: e.target.value })} required />
                </Field>
                <Field label="Description">
                  <input style={s.inp} placeholder="Brief description" value={editStore.description || ''} onChange={e => setEditStore({ ...editStore, description: e.target.value })} />
                </Field>

                <div style={s.divider} />
                <p style={s.sectionLabel}>Contact</p>
                <div style={s.coordRow}>
                  <Field label="Phone"><input style={s.inp} placeholder="+27…" value={editStore.phone || ''} onChange={e => setEditStore({ ...editStore, phone: e.target.value })} /></Field>
                  <Field label="Email"><input style={s.inp} type="email" placeholder="store@email.com" value={editStore.email || ''} onChange={e => setEditStore({ ...editStore, email: e.target.value })} /></Field>
                </div>

                <div style={s.divider} />
                <p style={s.sectionLabel}>Location</p>
                <Field label="Address"><input style={s.inp} placeholder="Street address" value={editStore.address?.line1 || ''} onChange={e => setEditStore({ ...editStore, address: { ...editStore.address, line1: e.target.value } })} /></Field>
                <Field label="City"><input style={s.inp} placeholder="City" value={editStore.address?.city || ''} onChange={e => setEditStore({ ...editStore, address: { ...editStore.address, city: e.target.value } })} /></Field>
                <div style={s.coordRow}>
                  <Field label="Latitude"><input style={s.inp} type="number" step="any" value={editStore.location?.coordinates?.[1] || ''} onChange={e => setEditStore({ ...editStore, location: { type: 'Point', coordinates: [editStore.location?.coordinates?.[0] || 0, Number(e.target.value)] } })} /></Field>
                  <Field label="Longitude"><input style={s.inp} type="number" step="any" value={editStore.location?.coordinates?.[0] || ''} onChange={e => setEditStore({ ...editStore, location: { type: 'Point', coordinates: [Number(e.target.value), editStore.location?.coordinates?.[1] || 0] } })} /></Field>
                </div>

                <div style={s.divider} />
                <Field label="Status">
                  <select style={s.inp} value={editStore.isActive ? 'active' : 'inactive'} onChange={e => setEditStore({ ...editStore, isActive: e.target.value === 'active' })}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </Field>

                <div style={s.divider} />
                <p style={s.sectionLabel}>Payments</p>
                <div style={s.paymentBanner(hasPaystackSubaccount(editStore))}>
                  <strong>
                    {hasPaystackSubaccount(editStore)
                      ? 'Paystack subaccount saved and hidden.'
                      : 'No Paystack subaccount saved yet.'}
                  </strong>
                  <span>
                    {hasPaystackSubaccount(editStore)
                      ? 'Paste a new Paystack ACCT_... code below only if you want to replace the existing one.'
                      : "Paste the store's Paystack ACCT_... code to enable routed payments for this store."}
                  </span>
                </div>
                <Field
                  label="Replace Paystack Subaccount Code"
                  hint={
                    hasPaystackSubaccount(editStore)
                      ? 'A code is saved and hidden. Paste a new Paystack code only if you need to replace it.'
                      : 'No Paystack code is saved yet. Paste the code from Paystack to route payments for this store.'
                  }
                >
                  <input
                    style={s.inpMono}
                    placeholder={hasPaystackSubaccount(editStore) ? 'Saved code hidden' : 'ACCT_xxxxxxxxxxxxxxxx'}
                    value={editStore.paystackSubaccountReplacement || ''}
                    onChange={e => setEditStore({ ...editStore, paystackSubaccountReplacement: e.target.value })}
                  />
                </Field>

                <div style={s.divider} />
                <p style={s.sectionLabel}>Cart Rules</p>
                <Field label="Max Items Per Cart" hint="Leave blank for unlimited. Customers cannot add more than this many items in a single session at this store.">
                  <input style={s.inp} type="number" min="1" step="1" placeholder="Unlimited" value={editStore.settings?.maxCartQuantity || ''} onChange={e => setEditStore({ ...editStore, settings: { ...editStore.settings, maxCartQuantity: e.target.value } })} />
                </Field>
                <Toggle
                  label="Weighed products"
                  hint="Enable if this store sells items priced by weight (e.g. deli, produce)"
                  value={editStore.settings?.supportsWeighedProducts === true}
                  onChange={v => setEditStore({ ...editStore, settings: { ...editStore.settings, supportsWeighedProducts: v } })}
                />

                <div style={s.divider} />
                <p style={s.sectionLabel}>Logo</p>
                <LogoUploadField
                  currentLogo={editStore.logo ?? null}
                  storeId={editStore._id}
                  token={token}
                  onUploaded={logo => setEditStore((prev: any) => ({ ...prev, logo }))}
                  onPendingFile={() => {}}
                />
              </div>
              <div style={s.modalFooter}>
                <button type="button" style={s.cancelBtn} onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="submit" style={s.saveBtn}>Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stores;
